import React, { useEffect, useMemo, useRef, useState } from 'react';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { listarCatalogo } from './api/catalogo';
import './SimuladorPricing.css';
import { Search, X, TrendingUp, TrendingDown, Target, Zap, Bot, RotateCcw, ChevronDown, Plus, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const functions = getFunctions();
const claudePMFn = httpsCallable(functions, 'consultarClaudePM', { timeout: 170000 });

// ── Helpers ──────────────────────────────────────────────────
const clpFmt = n => n ? `$${new Intl.NumberFormat('es-CL').format(Math.round(n))}` : '—';
const clpM = n => n ? `$${(n / 1_000_000).toFixed(2)}M` : '—';
const toNum = v => typeof v === 'number' ? v : Number(String(v || '0').replace(/[^0-9]/g, '')) || 0;
const normStr = (s = '') => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

// Extrae precio por tipo
const getPrecioByTipo = (auto, tipoBuscado) => {
    const tipos = Array.isArray(auto?.tiposprecio) ? auto.tiposprecio : [];
    const precios = Array.isArray(auto?.precio) ? auto.precio : [];
    const idx = tipos.findIndex(t => String(t).toLowerCase() === String(tipoBuscado).toLowerCase());
    return idx >= 0 ? toNum(precios[idx]) : 0;
};

// ─────────────────────────────────────────────────────────────
function SimuladorPricing() {
    // Búsqueda modelo base
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const searchTimer = useRef(null);
    const catalogoRef = useRef(null); // cache del catálogo completo — evita refetch en cada búsqueda/cambio de segmento

    const obtenerCatalogoCompleto = async () => {
        if (!catalogoRef.current) {
            const { autos } = await listarCatalogo({ todos: true });
            catalogoRef.current = autos;
        }
        return catalogoRef.current;
    };

    // Modelo seleccionado y segmento
    const [modeloBase, setModeloBase] = useState(null);
    const [precioLista, setPrecioLista] = useState(0);   // precio lista actual
    const [precioSimulado, setPrecioSimulado] = useState(0);   // precio lista editado
    const [tipoListaKey, setTipoListaKey] = useState('');  // nombre exacto del tipo lista
    const [segmento, setSegmento] = useState([]);
    const [loadingSegmento, setLoadingSegmento] = useState(false);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
    const [categoriasDisponibles, setCategoriasDisponibles] = useState([]);

    // Agregar/quitar competidores manualmente del segmento
    const [compBusqueda, setCompBusqueda] = useState('');
    const [compResultados, setCompResultados] = useState([]);
    const [compBuscando, setCompBuscando] = useState(false);
    const compTimerRef = useRef(null);

    // IA
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResponse, setAiResponse] = useState('');

    // ── Dropdown ────────────────────────────────────────────────
    useEffect(() => {
        const out = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setDropdownOpen(false); };
        const repos = () => { if (dropdownOpen && inputRef.current) { const r = inputRef.current.getBoundingClientRect(); setDropdownPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width }); } };
        document.addEventListener('mousedown', out);
        window.addEventListener('scroll', repos, true);
        window.addEventListener('resize', repos);
        return () => { document.removeEventListener('mousedown', out); window.removeEventListener('scroll', repos, true); window.removeEventListener('resize', repos); };
    }, [dropdownOpen]);

    useEffect(() => {
        if (!searchText.trim()) { setSearchResults([]); setDropdownOpen(false); return; }
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => buscarModelos(searchText), 300);
        return () => clearTimeout(searchTimer.current);
    }, [searchText]);

    const calcPos = () => {
        if (!inputRef.current) return;
        const r = inputRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
    };

    const buscarModelos = async texto => {
        setSearchLoading(true); calcPos(); setDropdownOpen(true);
        try {
            const q = normStr(texto);
            const catalogo = await obtenerCatalogoCompleto();
            const resultados = catalogo.filter(a => normStr(`${a.marca} ${a.model} ${a.modelDetail}`).includes(q));
            setSearchResults(resultados.slice(0, 20));
        } catch (e) { console.error(e); setSearchResults([]); }
        finally { setSearchLoading(false); }
    };

    // ── Seleccionar modelo base ─────────────────────────────────
    const seleccionarModelo = async modelo => {
        setModeloBase(modelo);
        setSearchText(''); setDropdownOpen(false);
        setAiResponse('');

        // Extraer solo el precio lista
        const tipos = modelo.tiposprecio || [];
        const precios = modelo.precio || [];
        let pLista = 0, keyLista = '';
        const idxLista = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
        if (idxLista >= 0) {
            pLista = toNum(precios[idxLista]);
            keyLista = tipos[idxLista];
        } else if (precios.length) {
            // fallback: primer precio
            pLista = toNum(precios[0]);
            keyLista = tipos[0] || 'Precio Lista';
        }
        setPrecioLista(pLista);
        setPrecioSimulado(pLista);
        setTipoListaKey(keyLista);
        setCategoriaSeleccionada(modelo.categoria || '');
        cargarCategorias(); // cargar lista de categorías disponibles

        // Carga inicial del segmento — reutiliza cambiarSegmento. Se pasan
        // pLista/keyLista/modelo explícitos porque el estado (precioLista,
        // tipoListaKey, modeloBase) todavía no se actualizó — React batchea
        // los setState de arriba, así que leerlos del closure acá daría
        // valores viejos (0 / '' / null en la primera selección).
        if (modelo.categoria) {
            cambiarSegmento(modelo.categoria, { precioBase: pLista, tipoKey: keyLista, modeloBase: modelo });
        }
    };

    const limpiar = () => {
        setModeloBase(null); setPrecioLista(0); setPrecioSimulado(0);
        setTipoListaKey(''); setSegmento([]); setAiResponse('');
        setCategoriaSeleccionada(''); setCategoriasDisponibles([]);
        setSearchText('');
    };

    // Cargar categorías disponibles y cambiar segmento manualmente
    const cargarCategorias = async () => {
        try {
            const catalogo = await obtenerCatalogoCompleto();
            const cats = [...new Set(catalogo.map(a => a.categoria).filter(Boolean))].sort();
            setCategoriasDisponibles(cats);
        } catch (e) { console.error(e); }
    };

    // Extrae el precio "lista" de un modelo probando primero el tipo exacto
    // del modelo base y, si no está, cualquier tipo que contenga "lista".
    const extraerPrecioLista = (m, tipoKey) => {
        const directo = getPrecioByTipo(m, tipoKey);
        if (directo) return directo;
        const tipos = m.tiposprecio || [];
        const precios = m.precio || [];
        const idx = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
        return idx >= 0 ? toNum(precios[idx]) : toNum(precios[0]);
    };

    const CERCANOS_POR_LADO = 5; // por defecto: 5 más baratos + 5 más caros

    const cambiarSegmento = async (nuevaCat, opts = {}) => {
        setCategoriaSeleccionada(nuevaCat);
        if (!nuevaCat) { setSegmento([]); return; }
        setSegmento([]);
        setLoadingSegmento(true);
        try {
            const precioBase = opts.precioBase ?? precioLista;
            const tipoKey = opts.tipoKey ?? tipoListaKey;
            const baseModelo = opts.modeloBase ?? modeloBase;

            const catalogo = await obtenerCatalogoCompleto();
            const candidatos = catalogo
                .filter(a => a.categoria === nuevaCat && !esMismoAuto(a, baseModelo))
                .map(m => ({ ...m, _precioTmp: extraerPrecioLista(m, tipoKey) }))
                .filter(m => m._precioTmp > 0);

            let resultado;
            if (precioBase > 0) {
                // Por defecto: los N más cercanos en precio, hacia arriba y hacia abajo —
                // no toda la categoría (puede tener decenas de modelos irrelevantes en precio).
                const masBaratos = candidatos.filter(m => m._precioTmp < precioBase)
                    .sort((a, b) => b._precioTmp - a._precioTmp).slice(0, CERCANOS_POR_LADO);
                const masCaros = candidatos.filter(m => m._precioTmp >= precioBase)
                    .sort((a, b) => a._precioTmp - b._precioTmp).slice(0, CERCANOS_POR_LADO);
                resultado = [...masBaratos, ...masCaros];
            } else {
                resultado = candidatos; // sin precio base conocido — no hay forma de medir "cercanía"
            }

            console.log(`📊 Segmento "${nuevaCat}": ${resultado.length} modelos cercanos en precio (de ${candidatos.length} en la categoría)`);
            setSegmento(resultado.map(({ _precioTmp, ...m }) => m));
        } catch (e) {
            console.error('Error cargando segmento:', e);
            setSegmento([]);
        } finally {
            setLoadingSegmento(false);
        }
    };

    // ── Competidores manuales — agregar/quitar del segmento actual ──
    const esMismoAuto = (a, b) => !!a && !!b && a.marca === b.marca && a.model === b.model && a.modelDetail === b.modelDetail;

    useEffect(() => {
        if (!compBusqueda.trim()) { setCompResultados([]); return; }
        clearTimeout(compTimerRef.current);
        compTimerRef.current = setTimeout(async () => {
            setCompBuscando(true);
            try {
                const q = normStr(compBusqueda);
                const catalogo = await obtenerCatalogoCompleto();
                const resultados = catalogo.filter(a =>
                    normStr(`${a.marca} ${a.model} ${a.modelDetail}`).includes(q) &&
                    !esMismoAuto(a, modeloBase) &&
                    !segmento.some(m => esMismoAuto(m, a))
                );
                setCompResultados(resultados.slice(0, 8));
            } catch (e) { console.error(e); setCompResultados([]); }
            finally { setCompBuscando(false); }
        }, 250);
        return () => clearTimeout(compTimerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compBusqueda, segmento, modeloBase]);

    const agregarCompetidor = (modelo) => {
        setSegmento(prev => [...prev, modelo]);
        setCompBusqueda(''); setCompResultados([]);
    };
    const quitarCompetidor = (modelo) => {
        setSegmento(prev => prev.filter(m => !esMismoAuto(m, modelo)));
    };
    const vaciarSegmento = () => { setSegmento([]); setCategoriaSeleccionada(''); };

    const resetearPrecios = () => setPrecioSimulado(precioLista);

    const actualizarPrecio = valor => {
        const num = toNum(String(valor).replace(/[^0-9]/g, ''));
        setPrecioSimulado(num);
    };

    // ── Calcular ranking con precio lista ───────────────────────
    const rankingData = useMemo(() => {
        if (!modeloBase || !precioLista || !segmento.length) return null;

        const precioSim = precioSimulado || precioLista;
        const precioAct = precioLista;

        // Armar lista del segmento con precio lista
        const lista = segmento
            .map(m => {
                const p = getPrecioByTipo(m, tipoListaKey) ||
                    // fallback: buscar cualquier tipo que contenga "lista"
                    (() => {
                        const tipos = m.tiposprecio || [];
                        const precios = m.precio || [];
                        const idx = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
                        return idx >= 0 ? toNum(precios[idx]) : toNum(precios[0]);
                    })();
                return p > 0 ? { ...m, _precio: p } : null;
            })
            .filter(Boolean);

        // Reemplazar precio del modelo base con el simulado
        const listaConSim = lista.map(m => {
            if (m.marca === modeloBase.marca && m.model === modeloBase.model && m.modelDetail === modeloBase.modelDetail)
                return { ...m, _precio: precioSim, _esBase: true };
            return m;
        });

        if (!listaConSim.find(m => m._esBase))
            listaConSim.push({ ...modeloBase, _precio: precioSim, _esBase: true });

        listaConSim.sort((a, b) => a._precio - b._precio);
        const maxPrecio = Math.max(...listaConSim.map(m => m._precio));

        const posSimulada = listaConSim.findIndex(m => m._esBase) + 1;

        // Posición con precio actual (sin simulación)
        const listaActual = listaConSim.map(m => m._esBase ? { ...m, _precio: precioAct } : m)
            .sort((a, b) => a._precio - b._precio);
        const posActual = listaActual.findIndex(m => m._esBase) + 1;

        const menorComp = listaConSim.filter(m => !m._esBase).sort((a, b) => a._precio - b._precio)[0];
        const precioParaLiderar = menorComp ? menorComp._precio - 1 : 0;

        const porEncima = listaConSim.filter(m => !m._esBase && m._precio > precioSim).length;
        const porDebajo = listaConSim.filter(m => !m._esBase && m._precio < precioSim).length;

        return { lista: listaConSim, maxPrecio, posSimulada, posActual, porEncima, porDebajo, precioParaLiderar, total: listaConSim.length, precioSim, precioAct };
    }, [modeloBase, precioLista, precioSimulado, tipoListaKey, segmento]);

    // ── Precio mínimo para liderar (precio lista) ───────────────
    const precioMinLiderar = useMemo(() => {
        if (!modeloBase || !segmento.length) return 0;
        const competidores = segmento
            .filter(m => !(m.marca === modeloBase.marca && m.model === modeloBase.model && m.modelDetail === modeloBase.modelDetail))
            .map(m => {
                const p = getPrecioByTipo(m, tipoListaKey) ||
                    (() => {
                        const tipos = m.tiposprecio || [];
                        const precios = m.precio || [];
                        const idx = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
                        return idx >= 0 ? toNum(precios[idx]) : toNum(precios[0]);
                    })();
                return p;
            })
            .filter(p => p > 0);
        return competidores.length ? Math.min(...competidores) - 1 : 0;
    }, [modeloBase, segmento, tipoListaKey]);

    // ── Análisis IA ─────────────────────────────────────────────
    const analizarImpacto = async () => {
        if (!modeloBase || !rankingData) return;
        setAiLoading(true);
        try {
            const diff = precioSimulado - precioLista;
            const pctDiff = precioLista ? ((diff / precioLista) * 100).toFixed(1) : '0';
            const cambio = `${tipoListaKey || 'Precio Lista'}: ${clpFmt(precioLista)} → ${clpFmt(precioSimulado)} (${diff > 0 ? '+' : ''}${pctDiff}%)`;

            const prompt = `Analiza el impacto estratégico de este cambio de Precio Lista:

Modelo: ${modeloBase.marca} ${modeloBase.model} ${modeloBase.modelDetail}
Segmento: ${modeloBase.categoria} (${rankingData.total} modelos competidores)
Cambio propuesto: ${cambio}
Posición actual: #${rankingData.posActual} de ${rankingData.total}
Posición simulada: #${rankingData.posSimulada} de ${rankingData.total}
Modelos más caros: ${rankingData.porEncima} · Modelos más baratos: ${rankingData.porDebajo}
Precio para liderar el segmento: ${clpFmt(rankingData.precioParaLiderar)}

¿Es estratégicamente recomendable este ajuste? Considera: impacto en percepción de valor, 
posicionamiento competitivo, y si la posición ganada justifica el ajuste.
Dame máximo 3 recomendaciones ejecutivas concretas.`;

            const result = await claudePMFn({ prompt, uid: auth.currentUser?.uid });
            setAiResponse(result.data?.response || '');
        } catch (e) { console.error(e); setAiResponse('Error al generar el análisis.'); }
        finally { setAiLoading(false); }
    };

    // ── Determinar si hay cambios ───────────────────────────────
    const hayBancambios = precioSimulado !== precioLista && precioSimulado > 0;

    // ── RENDER ──────────────────────────────────────────────────
    return (
        <div className="sim-main">
            <div className="sim-wrapper">

                {/* Header */}
                <div>
                    <h1 className="sim-page-title">Simulador de Pricing</h1>
                    <p className="sim-page-sub">
                        Simula cambios de precio y visualiza el impacto en el ranking competitivo del segmento
                    </p>
                </div>

                {/* ── Step 1: Buscar modelo ── */}
                <div className="sim-card">
                    <div className="sim-card-header">
                        <div className="sim-card-title">
                            <div className="sim-card-icon" style={{ background: '#eff6ff', color: '#2563eb' }}><Search size={13} /></div>
                            {modeloBase ? 'Modelo seleccionado' : '1. Selecciona un modelo'}
                        </div>
                        {modeloBase && (
                            <button className="sim-btn sim-btn--ghost" onClick={limpiar}>
                                <X size={13} /> Cambiar
                            </button>
                        )}
                    </div>
                    <div className="sim-card-body">
                        {!modeloBase ? (
                            <div className="sim-search-wrap" ref={searchRef}>
                                <span className="sim-search-icon"><Search size={14} /></span>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="sim-search-input"
                                    placeholder="Busca el modelo que quieres simular…"
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    onFocus={calcPos} onClick={calcPos}
                                />
                                {dropdownOpen && (
                                    <div className="sim-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
                                        {searchLoading && <div className="sim-dropdown-empty">Buscando…</div>}
                                        {!searchLoading && !searchResults.length && searchText && <div className="sim-dropdown-empty">Sin resultados para "{searchText}"</div>}
                                        {!searchLoading && searchResults.map(r => (
                                            <div key={r.id} className="sim-dropdown-item" onClick={() => seleccionarModelo(r)}>
                                                <span className="sim-dropdown-marca">{r.marca}</span>
                                                <span className="sim-dropdown-nombre">{r.model}</span>
                                                <span className="sim-dropdown-version">{r.modelDetail}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="sim-modelo-tag">
                                    <span className="sim-modelo-marca">{modeloBase.marca}</span>
                                    <span className="sim-modelo-name">{modeloBase.model}</span>
                                    <span className="sim-modelo-version">{modeloBase.modelDetail}</span>
                                </div>

                                {/* Segmento de comparación — siempre visible */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, flexShrink: 0 }}>
                                        Segmento:
                                    </span>
                                    {categoriasDisponibles.length > 0 ? (
                                        <select
                                            value={categoriaSeleccionada}
                                            onChange={e => cambiarSegmento(e.target.value)}
                                            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#374151', cursor: 'pointer', fontFamily: 'inherit', flex: 1, minWidth: 180, maxWidth: 280 }}
                                        >
                                            <option value="">— Elige el segmento a comparar —</option>
                                            {categoriasDisponibles.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Cargando categorías…</span>
                                    )}
                                    {loadingSegmento && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#94a3b8' }}>
                                            <div className="sim-spinner" /> Cargando…
                                        </span>
                                    )}
                                    {!loadingSegmento && categoriaSeleccionada && segmento.length > 0 && (
                                        <span style={{ fontSize: 12, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '3px 10px', borderRadius: 999, fontWeight: 600 }}>
                                            {segmento.length} modelos
                                        </span>
                                    )}
                                    {!loadingSegmento && categoriaSeleccionada && segmento.length === 0 && (
                                        <span style={{ fontSize: 12, color: '#f59e0b' }}>Sin modelos para esta categoría</span>
                                    )}
                                </div>
                                {categoriaSeleccionada && (
                                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0' }}>
                                        Comparando contra los modelos con precio más cercano de "{categoriaSeleccionada}" (hacia arriba y hacia abajo) — agrega o quita competidores abajo para ajustar la lista.
                                    </p>
                                )}

                                {/* Competidores — agregar/quitar manualmente */}
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Competidores ({segmento.length})</span>
                                        {segmento.length > 0 && (
                                            <button className="sim-btn sim-btn--ghost" style={{ fontSize: 11, padding: '3px 8px', marginLeft: 'auto' }} onClick={vaciarSegmento}>
                                                <Trash2 size={11} /> Vaciar lista
                                            </button>
                                        )}
                                    </div>

                                    <div style={{ position: 'relative', marginBottom: 8 }}>
                                        <input
                                            type="text"
                                            value={compBusqueda}
                                            onChange={e => setCompBusqueda(e.target.value)}
                                            placeholder="Buscar un modelo para agregar como competidor…"
                                            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}
                                        />
                                        {(compBuscando || compResultados.length > 0) && compBusqueda && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 50, maxHeight: 220, overflowY: 'auto' }}>
                                                {compBuscando && <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>Buscando…</div>}
                                                {!compBuscando && compResultados.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>Sin resultados</div>}
                                                {!compBuscando && compResultados.map(r => (
                                                    <div key={r.id} onClick={() => agregarCompetidor(r)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f8fafc' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                        <Plus size={12} style={{ color: '#16a34a', flexShrink: 0 }} />
                                                        <span style={{ fontWeight: 600 }}>{r.marca}</span>
                                                        <span>{r.model}</span>
                                                        <span style={{ color: '#94a3b8', fontSize: 11 }}>{r.modelDetail}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {segmento.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 160, overflowY: 'auto', padding: '2px' }}>
                                            {segmento.map(m => (
                                                <span key={`${m.marca}-${m.model}-${m.modelDetail}`}
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 6px 4px 10px', borderRadius: 999, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151' }}>
                                                    <span style={{ fontWeight: 600 }}>{m.marca}</span> {m.model} <span style={{ color: '#94a3b8' }}>{m.modelDetail}</span>
                                                    <button onClick={() => quitarCompetidor(m)} title="Quitar"
                                                        style={{ display: 'flex', padding: 2, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer' }}>
                                                        <X size={12} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Step 2: Editar precio lista ── */}
                {modeloBase && precioLista > 0 && (
                    <div className="sim-card">
                        <div className="sim-card-header">
                            <div className="sim-card-title">
                                <div className="sim-card-icon" style={{ background: '#fef3c7', color: '#92400e' }}><Zap size={13} /></div>
                                2. Simula el Precio Lista
                                {hayBancambios && (
                                    <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 5, fontWeight: 600 }}>
                                        Con cambios
                                    </span>
                                )}
                            </div>
                            {hayBancambios && (
                                <button className="sim-btn" onClick={resetearPrecios}>
                                    <RotateCcw size={13} /> Restablecer
                                </button>
                            )}
                        </div>
                        <div className="sim-card-body">
                            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>

                                {/* Input principal */}
                                <div className="sim-precio-field" style={{ minWidth: 260 }}>
                                    <div className="sim-precio-label">
                                        {tipoListaKey || 'Precio Lista'}
                                        <span className="sim-precio-original">actual: {clpFmt(precioLista)}</span>
                                    </div>
                                    <div className="sim-precio-input-wrap">
                                        <span className="sim-precio-prefix">$</span>
                                        <input
                                            type="text"
                                            className={`sim-precio-input${hayBancambios ? ' changed' : ''}`}
                                            value={precioSimulado > 0 ? new Intl.NumberFormat('es-CL').format(precioSimulado) : ''}
                                            onChange={e => actualizarPrecio(e.target.value)}
                                            placeholder={new Intl.NumberFormat('es-CL').format(precioLista)}
                                            style={{ fontSize: 18, fontWeight: 700, padding: '12px 12px 12px 22px' }}
                                            autoFocus
                                        />
                                    </div>
                                    {hayBancambios && (() => {
                                        const diff = precioSimulado - precioLista;
                                        const pctDiff = (diff / precioLista) * 100;
                                        return (
                                            <div className={`sim-precio-delta sim-precio-delta--${diff > 0 ? 'up' : 'down'}`}>
                                                {diff > 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                                                {diff > 0 ? '+' : ''}{clpM(diff)} · {pctDiff > 0 ? '+' : ''}{pctDiff.toFixed(1)}%
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Sugerencias rápidas */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                                        Ajuste rápido
                                    </span>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {[-5, -3, -2, -1, 1, 2, 3, 5].map(p => (
                                            <button
                                                key={p}
                                                className="sim-btn"
                                                style={{ fontSize: 12, padding: '5px 10px', color: p < 0 ? '#16a34a' : '#dc2626' }}
                                                onClick={() => setPrecioSimulado(Math.round(precioLista * (1 + p / 100)))}
                                            >
                                                {p > 0 ? '+' : ''}{p}%
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Precio para liderar */}
                                {precioMinLiderar > 0 && precioMinLiderar < precioLista && (
                                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px' }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                                            Para ser el más barato
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'DM Mono, monospace', color: '#0f172a' }}>
                                            {clpFmt(precioMinLiderar)}
                                        </div>
                                        <button
                                            className="sim-btn"
                                            style={{ marginTop: 8, fontSize: 11, padding: '4px 10px', background: '#dcfce7', borderColor: '#86efac', color: '#166534' }}
                                            onClick={() => setPrecioSimulado(precioMinLiderar)}
                                        >
                                            Aplicar este precio
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Step 3: Impacto en posición ── */}
                {modeloBase && precioLista > 0 && !loadingSegmento && segmento.length === 0 && (
                    <div className="sim-card">
                        <div className="sim-card-body" style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>
                            <Target size={24} style={{ opacity: .3, marginBottom: 8 }} />
                            <p style={{ fontSize: 13 }}>No se pudo cargar el segmento competitivo.</p>
                            <p style={{ fontSize: 12 }}>Verifica que el modelo tenga categoría asignada en el catálogo.</p>
                        </div>
                    </div>
                )}
                {rankingData && (
                    <>
                        {/* Resumen posición */}
                        <div className="sim-card">
                            <div className="sim-card-header">
                                <div className="sim-card-title">
                                    <div className="sim-card-icon" style={{ background: '#f0fdf4', color: '#166534' }}><Target size={13} /></div>
                                    3. Impacto en posición competitiva
                                </div>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                    {tipoListaKey || 'Precio Lista'}
                                </span>
                            </div>
                            <div className="sim-card-body">
                                <div className="sim-posicion-grid">
                                    {/* Posición actual */}
                                    <div className="sim-posicion-card">
                                        <div className="sim-posicion-tipo">Posición actual</div>
                                        <div className="sim-posicion-val">#{rankingData.posActual}</div>
                                        <div className="sim-posicion-total">de {rankingData.total}</div>
                                    </div>
                                    {/* Posición simulada */}
                                    <div className="sim-posicion-card" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                                        <div className="sim-posicion-tipo">Posición simulada</div>
                                        <div className="sim-posicion-val" style={{ color: rankingData.posSimulada < rankingData.posActual ? '#16a34a' : rankingData.posSimulada > rankingData.posActual ? '#dc2626' : '#0f172a' }}>
                                            #{rankingData.posSimulada}
                                        </div>
                                        <div className="sim-posicion-total">de {rankingData.total}</div>
                                        {rankingData.posSimulada !== rankingData.posActual && (
                                            <div className={`sim-posicion-flecha ${rankingData.posSimulada < rankingData.posActual ? 'mejor' : 'peor'}`}>
                                                {rankingData.posSimulada < rankingData.posActual
                                                    ? `▲ Sube ${rankingData.posActual - rankingData.posSimulada} posición${rankingData.posActual - rankingData.posSimulada !== 1 ? 'es' : ''}`
                                                    : `▼ Baja ${rankingData.posSimulada - rankingData.posActual} posición${rankingData.posSimulada - rankingData.posActual !== 1 ? 'es' : ''}`}
                                            </div>
                                        )}
                                        {rankingData.posSimulada === rankingData.posActual && (
                                            <div className="sim-posicion-flecha igual">= Sin cambio</div>
                                        )}
                                    </div>
                                    {/* Por encima */}
                                    <div className="sim-posicion-card">
                                        <div className="sim-posicion-tipo">Por encima</div>
                                        <div className="sim-posicion-val">{rankingData.porEncima}</div>
                                        <div className="sim-posicion-total">modelos más caros</div>
                                    </div>
                                    {/* Por debajo */}
                                    <div className="sim-posicion-card">
                                        <div className="sim-posicion-tipo">Por debajo</div>
                                        <div className="sim-posicion-val">{rankingData.porDebajo}</div>
                                        <div className="sim-posicion-total">modelos más baratos</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Precio mínimo para liderar */}
                        {precioMinLiderar > 0 && (
                            <div className="sim-card">
                                <div className="sim-card-header">
                                    <div className="sim-card-title">
                                        <div className="sim-card-icon" style={{ background: '#f0fdf4', color: '#059669' }}>🥇</div>
                                        Precio mínimo para liderar el segmento
                                    </div>
                                </div>
                                <div className="sim-card-body">
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <div className="sim-minprecio-item" style={{ flex: 1, minWidth: 180 }}>
                                            <div className="sim-minprecio-tipo">{tipoListaKey || 'Precio Lista'}</div>
                                            <div className="sim-minprecio-val">{clpFmt(precioMinLiderar)}</div>
                                            <div className="sim-minprecio-sub">Tu precio simulado: {clpFmt(precioSimulado)}</div>
                                            <div className={`sim-minprecio-diff ${precioSimulado <= precioMinLiderar ? 'already' : 'need-down'}`}>
                                                {precioSimulado <= precioMinLiderar
                                                    ? '✓ Eres el más barato del segmento'
                                                    : `Necesitas bajar ${clpFmt(precioSimulado - precioMinLiderar)} más`}
                                            </div>
                                        </div>
                                        {precioSimulado > precioMinLiderar && (
                                            <button
                                                className="sim-btn"
                                                style={{ background: '#f0fdf4', borderColor: '#86efac', color: '#166634' }}
                                                onClick={() => setPrecioSimulado(precioMinLiderar)}
                                            >
                                                Aplicar precio líder
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Ranking visual */}
                        <div className="sim-card">
                            <div className="sim-card-header">
                                <div className="sim-card-title">
                                    <div className="sim-card-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}><TrendingUp size={13} /></div>
                                    Ranking del segmento — Precio Lista
                                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>
                                        {rankingData.lista.length} modelos · {modeloBase.categoria}
                                    </span>
                                </div>
                            </div>
                            <div className="sim-ranking-wrap">
                                {rankingData.lista.map((m, i) => {
                                    const isBase = m._esBase;
                                    const pct = rankingData.maxPrecio ? (m._precio / rankingData.maxPrecio) * 100 : 0;
                                    return (
                                        <div
                                            key={`${m.marca}-${m.model}-${m.modelDetail}`}
                                            className={`sim-ranking-item${isBase && hayBancambios ? ' highlight-simulated' : isBase ? ' highlight' : ''}`}
                                        >
                                            <span className={`sim-ranking-pos${i < 3 ? ' top3' : ''}`}>
                                                {i < 3 ? ['🥇', '🥈', '🥉'][i] : `#${i + 1}`}
                                            </span>
                                            <div className="sim-ranking-info">
                                                <div className="sim-ranking-name">{m.marca} {m.model}</div>
                                                <div className="sim-ranking-detail">{m.modelDetail}</div>
                                            </div>
                                            <div className="sim-ranking-bar-wrap">
                                                <div
                                                    className="sim-ranking-bar"
                                                    style={{
                                                        width: `${pct}%`,
                                                        background: isBase
                                                            ? (hayBancambios ? '#f59e0b' : '#2563eb')
                                                            : `hsl(${220 - i * 8}, 70%, ${55 + i * 2}%)`,
                                                    }}
                                                />
                                            </div>
                                            <span className="sim-ranking-price">{clpFmt(m._precio)}</span>
                                            {isBase && (
                                                <span className={`sim-ranking-badge${hayBancambios ? ' sim-ranking-badge--sim' : ' sim-ranking-badge--actual'}`}>
                                                    {hayBancambios ? 'SIMULADO' : 'TU MODELO'}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* IA */}
                        {(aiResponse || aiLoading) ? (
                            <div className="sim-ai-card">
                                <div className="sim-ai-header">
                                    <div className="sim-ai-title"><Bot size={14} style={{ color: '#60a5fa' }} /> Análisis de impacto — PM</div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button className="sim-btn sim-btn--ai" style={{ fontSize: 12 }} onClick={analizarImpacto} disabled={aiLoading}>
                                            <RotateCcw size={12} /> Regenerar
                                        </button>
                                        <button className="sim-btn" style={{ fontSize: 12, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', color: '#94a3b8' }} onClick={() => setAiResponse('')}>
                                            Cerrar
                                        </button>
                                    </div>
                                </div>
                                {aiLoading ? (
                                    <div className="sim-ai-thinking">
                                        <div className="sim-ai-dot" /><div className="sim-ai-dot" /><div className="sim-ai-dot" />
                                        <span>Analizando el impacto del cambio de precio…</span>
                                    </div>
                                ) : (
                                    <div className="sim-ai-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{aiResponse}</ReactMarkdown></div>
                                )}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <button className="sim-btn sim-btn--ai" onClick={analizarImpacto} disabled={aiLoading}>
                                    <Bot size={14} /> Analizar impacto con IA
                                </button>
                            </div>
                        )}
                    </>
                )}

            </div>
        </div>
    );
}

export default SimuladorPricing;